import { Button, Col, CreateTableState, Grid, Typography } from 'gu-ui-kit'
import React, { useEffect, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import styles from 'modules/AccountsMarketplace/Products/subcomponents/Table/hooks/table.module.scss'
import { ROUTES } from 'constants/routes'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import clsx from 'clsx'
import formatMoney from 'utils/formatMoney'
import he from 'he'

export const useTableConfig = ({ setSorting, categoryId }, data = []) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation()
  const [columnVisibility, setColumnVisibility] = useState()
  const pathGen = useGeneratePathI18n()

  useEffect(() => {
    switch (true) {
      case breakpoints.xs:
        setColumnVisibility({ action: false, amount: false, price: false })
        break

      case breakpoints.sm:
        setColumnVisibility({ action: false })
        break
      default:
        setColumnVisibility({})
        break
    }
  }, [breakpoints])

  const tableConfig = {
    columns: [
      {
        id: 'name',
        header: () => (
          <Typography variant={'Body-20'} color={'Label-Primary'}>
            {t('accounts_marketplace::products.list.head.name')}
          </Typography>
        ),
        cell: ({
          row: {
            original: { name, id, amount, price, currency = 'USD' },
          },
        }) => (
          <Grid direction={'column'} gap={8}>
            <Grid wrap={false} gap={16} alignItems={'center'}>
              <Typography
                className={styles['table__product-name-link']}
                color={'Label-Primary'}
                variant={'Body-15'}
                component={Link}
                to={pathGen(ROUTES.ACCOUNTS_MARKETPLACE_PRODUCT, {
                  categoryId,
                  productId: id,
                })}
              >
                {he.decode(name)}
              </Typography>
            </Grid>
            {breakpoints.sm && (
              <Grid gap={24}>
                <Col sm={'6'}>
                  <Button
                    noWrap
                    className={styles['table__button-load-description']}
                    variant={'primary'}
                    size={'s'}
                    component={Link}
                    to={pathGen(ROUTES.BILLING_MARKETPLACE_PRODUCT_PAYMENT, {
                      productId: id,
                    })}
                  >
                    {t('accounts_marketplace::products.list.rows.order_now')}
                  </Button>
                </Col>
                {breakpoints.xs && (
                  <Col sm={'6'}>
                    <table className={styles['table__product-info']}>
                      <tbody>
                        <tr>
                          <td>
                            <Typography
                              variant={'Body-10'}
                              color={'Label-Secondary'}
                            >
                              {t(
                                'accounts_marketplace::products.list.head.amount',
                              )}
                            </Typography>
                          </td>
                          <td>
                            <Typography variant={'Body-10'}>
                              {amount}
                            </Typography>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <Typography
                              variant={'Body-10'}
                              color={'Label-Secondary'}
                            >
                              {t(
                                'accounts_marketplace::products.list.head.price',
                              )}
                            </Typography>
                          </td>
                          <td>
                            <Typography
                              variant={'Body-10'}
                              dangerouslySetInnerHTML={{
                                __html: formatMoney(price, currency),
                              }}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </Col>
                )}
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        id: 'amount',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::products.list.head.amount')}
          </Typography>
        ),

        cell: ({
          row: {
            original: { amount },
          },
        }) => <Typography variant={'Body-3'}>{amount}</Typography>,
      },
      {
        id: 'price',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::products.list.head.price')}
          </Typography>
        ),

        cell: ({
          row: {
            original: { price, currency },
          },
        }) => (
          <Typography
            variant={'Body-3'}
            noWrap
            component={
              <span
                dangerouslySetInnerHTML={{
                  __html: formatMoney(price, currency),
                }}
              />
            }
          />
        ),
      },
      {
        id: 'action',
        header: () => (
          <Typography variant={'Body-20'} color={'Label-Primary'} />
        ),

        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <Grid justifyContent={'end'}>
            <Button
              noWrap
              className={styles['table__button-load-description']}
              variant={'primary'}
              size={'s'}
              component={Link}
              to={pathGen(ROUTES.BILLING_MARKETPLACE_PRODUCT_PAYMENT, {
                productId: id,
              })}
            >
              {t('accounts_marketplace::products.list.rows.order_now')}
            </Button>
          </Grid>
        ),
      },
    ],
    data,
    state: {
      columnVisibility: { ...columnVisibility },
    },
    showHeader: data.length > 0,
    showFooter: false,
    className: clsx('margin-bottom-16', styles['table']),
    setSorting,
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
